import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { adminService } from '../../services/adminService'
import { ROUTES } from '../../config/constants'
import { devError } from '../../utils/devLog'
import AdminLayout from '../../components/admin/AdminLayout'

export default function AdminDashboard() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    try {
      setLoading(true)
      const response = await adminService.getStats()
      if (response.success) {
        setStats(response.stats)
      } else {
        setError('Failed to load statistics')
      }
    } catch (err) {
      setError(err.message || 'Failed to load statistics')
      devError(err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-pitch-green text-xl">Loading...</div>
        </div>
      </AdminLayout>
    )
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="text-red-400">{error}</div>
      </AdminLayout>
    )
  }

  const statCards = [
    {
      title: 'Total Submissions',
      value: stats?.submissions?.total || 0,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      icon: '📊',
    },
    {
      title: 'Pending',
      value: stats?.submissions?.pending || 0,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
      icon: '⏳',
    },
    {
      title: 'In Progress',
      value: stats?.submissions?.inProgress || 0,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      icon: '🎬',
    },
    {
      title: 'Completed',
      value: stats?.submissions?.completed || 0,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/30',
      icon: '✅',
    },
    {
      title: 'Delivered',
      value: stats?.submissions?.delivered || 0,
      color: 'text-pitch-green',
      bgColor: 'bg-pitch-green/10',
      borderColor: 'border-pitch-green/30',
      icon: '🚀',
    },
    {
      title: 'Recent (7 days)',
      value: stats?.submissions?.recent || 0,
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-500/10',
      borderColor: 'border-cyan-500/30',
      icon: '📅',
    },
  ]

  return (
    <AdminLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="mb-8">
          <h1 className="text-4xl font-black mb-2">
            <span className="text-white">Dashboard</span>
          </h1>
          <p className="text-gray-400">Overview of your submissions and orders</p>
        </div>

        {/* Revenue summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-pitch-green/10 border-2 border-pitch-green/30 rounded-xl p-6"
          >
            <p className="text-gray-400 text-sm mb-1">Total revenue</p>
            <p className="text-pitch-green text-2xl md:text-3xl font-black">
              ₹{(stats?.orders?.revenue ?? 0).toLocaleString('en-IN')}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-cyan-500/10 border-2 border-cyan-500/30 rounded-xl p-6"
          >
            <p className="text-gray-400 text-sm mb-1">Revenue (last 7 days)</p>
            <p className="text-cyan-400 text-2xl md:text-3xl font-black">
              ₹{(stats?.orders?.revenueLast7Days ?? 0).toLocaleString('en-IN')}
            </p>
          </motion.div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`${stat.bgColor} ${stat.borderColor} border-2 rounded-xl p-6`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-3xl">{stat.icon}</span>
                <span className={`${stat.color} text-3xl font-black`}>
                  {stat.value}
                </span>
              </div>
              <h3 className="text-gray-300 font-semibold">{stat.title}</h3>
            </motion.div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6">
          <Link
            to={ROUTES.ADMIN_SUBMISSIONS}
            className="bg-gradient-to-br from-stadium-black to-gray-900 border-2 border-pitch-green/20 rounded-xl p-6 hover:border-pitch-green/50 transition-all"
          >
            <h2 className="text-2xl font-bold text-white mb-2">View All Submissions</h2>
            <p className="text-gray-400">Manage and track all match submissions</p>
          </Link>

          <Link
            to={ROUTES.ADMIN_ORDERS}
            className="bg-gradient-to-br from-stadium-black to-gray-900 border-2 border-pitch-green/20 rounded-xl p-6 hover:border-pitch-green/50 transition-all"
          >
            <h2 className="text-2xl font-bold text-white mb-2">View Orders</h2>
            <p className="text-gray-400">Track payments and orders</p>
          </Link>
        </div>
      </motion.div>
    </AdminLayout>
  )
}
